import React from 'react';
import { Search, Filter, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTranslationDir } from '../../hooks/useTranslationDir';

export const TransactionFilters = ({
  filters,
  onFilterChange,
  onClearFilters,
  onQuickDateFilter,
  activeQuickFilter,
  showAdvancedFilters,
  onToggleAdvancedFilters,
  hasActiveFilters,
  uniqueUsers,
  uniquePaymentMethods,
  uniqueCategories,
  uniqueUnits,
  isLoading
}) => {
  const { t } = useTranslation();
  const { isRTL } = useTranslationDir();



  const quickFilters = [
    { id: 'today', label: t('transactions.today') },
    { id: 'yesterday', label: t('transactions.yesterday') },
    { id: 'last7Days', label: t('transactions.last7Days') },
    { id: 'last30Days', label: t('transactions.last30Days') },
    { id: 'thisMonth', label: t('transactions.thisMonth') },
    { id: 'lastMonth', label: t('transactions.lastMonth') }
  ];

  const typeFilters = [
    { key: 'all', label: t('common.all'), color: 'slate' },
    { key: 'income', label: t('transactions.income'), color: 'green' },
    { key: 'expense', label: t('transactions.expense'), color: 'red' }
  ];

  const getButtonClass = (isActive, color = 'slate') => {
    const baseClass = 'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200';
    const colorMap = {
      slate: isActive ? 'bg-slate-600 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
      green: isActive ? 'bg-green-600 text-white shadow-md' : 'bg-green-100 text-green-700 hover:bg-green-200',
      red: isActive ? 'bg-red-600 text-white shadow-md' : 'bg-red-100 text-red-700 hover:bg-red-200',
      blue: isActive ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
    };
    return `${baseClass} ${colorMap[color]}`;
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-2">
        {t('common.filters')}
      </h3>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search size={20} className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-slate-400`} />
          <input
            type="text"
            placeholder={t('common.searchPlaceholder')}
            value={filters.searchTerm || ''}
            onChange={(e) => onFilterChange('searchTerm', e.target.value)}
            className={`w-full ${isRTL ? 'pr-10 pl-5' : 'pl-10 pr-5'} py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200`}
          />
        </div>
      </div>

      {/* Type Filter Buttons */}
      <div className="mb-4">
        <div className="grid grid-cols-3 gap-3">
          {typeFilters.map((typeFilter) => (
            <button
              key={typeFilter.key}
              onClick={() => onFilterChange('type', typeFilter.key)}
              className={getButtonClass(filters.type === typeFilter.key, typeFilter.color)}
            >
              {typeFilter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Date Filters */}
      <div className="mb-1">
        {/* <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('common.quickFilters')}
        </label> */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Source Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              {t('transactions.source')}
            </label>
            <select
              value={filters.source || 'all'}
              onChange={(e) => onFilterChange('source', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="all">{t('common.all')}</option>
              <option value="requests">{t('transactions.booking')}</option>
              <option value="costs">{t('transactions.cost')}</option>
              <option value="tasks">{t('transactions.task')}</option>
            </select>
          </div>

          {/* User Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              {t('transactions.user')}
            </label>
            <select
              value={filters.userName || ''}
              onChange={(e) => onFilterChange('userName', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">{t('common.all')}</option>
              {uniqueUsers.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>
          </div>

          {/* Unit Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              {t('transactions.unit')}
            </label>
            <select
              value={filters.unitId || ''}
              onChange={(e) => onFilterChange('unitId', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">{t('common.all')}</option>
              {uniqueUnits && uniqueUnits.map(unit => (
                <option key={unit._id} value={unit._id}>{`${unit.unitCode} - ${unit.unitName}`}</option>
              ))}
            </select>
          </div>

          {/* Quick Date Filters Buttons */}
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-slate-600 mb-2">
              {t('transactions.quickDate')}
            </label>
            <div className="grid sm:grid-cols-3 lg:grid-cols-6 grid-cols-2 gap-2">
              {quickFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => onQuickDateFilter(filter.id)}
                  className={getButtonClass(activeQuickFilter === filter.id, 'blue')}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Clear Filters & Loading */}
      <div className="flex items-center justify-between">

        {/* Advanced Filters Toggle */}
        <div className="mt-4">
          <button
            onClick={onToggleAdvancedFilters}
            className={`flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200 ${showAdvancedFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : ''
              }`}
          >
            <Filter size={16} />
            <span>{showAdvancedFilters ? t('hideFilters') : t('moreFilters')}</span>
          </button>
        </div>
        <div>
          {isLoading && <Loader2 size={20} className="animate-spin" />}
        </div>
        <button
          disabled={!hasActiveFilters}
          onClick={onClearFilters}
          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {hasActiveFilters && (
            <>
              <X size={16} />
              <span>{t('common.clearFilters')}</span>
            </>
          )}
        </button>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('dateFrom')}
              </label>
              <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('dateTo')}
              </label>
              <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => onFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('transactions.paymentMethod')}
              </label>
              <select
                value={filters.paymentMethod || ''}
                onChange={(e) => onFilterChange('paymentMethod', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('common.all')}</option>
                {uniquePaymentMethods.map(method => (
                  <option key={method} value={method}>{t(method)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('transactions.category')}
              </label>
              <select
                value={filters.categoryId || ''}
                onChange={(e) => onFilterChange('categoryId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('common.all')}</option>
                {uniqueCategories.map(category => (
                  <option key={category} value={category}>{t(category === "deposit" ? `transactions.${category}` : category)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};
